import { type CSSProperties } from "react";
import { Ban, Hand, TimerOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dot } from "@/components/ui/dot";
import { OP_STATUS_BLOCK, productsLabel } from "./production-order-utils";
import { BlockTooltip, useHoverTooltip } from "./BlockTooltip";
import { isOverrun, overrunGeometry, overrunLabel } from "./production-timeline-geometry";
import { blockRangeGeometryPct, type TimelineRange } from "./timeline-range";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// Bloco de uma OP na swimlane, recortado à faixa visível (multi-dia aparece em todo
// período que cobre). Cor por status; conflito → borda tracejada danger. Clicar abre o modal da OP.
// Extraído de TimelineGrid (Guardrail #1). `now` alimenta a renderização de overrun.
interface Props {
  o: ProductionOrder;
  range: TimelineRange;
  conflict: boolean;
  now: Date;
  onOpenOrder: (o: ProductionOrder) => void;
}

export function TimelineBlock({ o, range, conflict, now, onOpenOrder }: Props) {
  // OP em overrun: a barra estica de plannedStartAt até "agora" (og.bar), nunca mutando
  // expectedEnd. Não-overrun mantém a geometria da janela planejada.
  const overrun = isOverrun(o, now);
  const og = overrun ? overrunGeometry(o.plannedStartAt, o.expectedEnd, now, range) : null;
  const geo = og?.bar ?? blockRangeGeometryPct(o.plannedStartAt, o.expectedEnd, range);
  const { tip, triggerProps } = useHoverTooltip();
  if (!geo) return null;
  const block = OP_STATUS_BLOCK[o.status];
  const cancelled = o.status === "CANCELLED";
  const delay = overrun ? overrunLabel(o.expectedEnd, now) : "";
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenOrder(o);
        }}
        {...triggerProps}
        // Largura em % com folga de 3px entre blocos; min-w garante legibilidade de slivers curtos.
        style={{ "--l": `${geo.leftPct}%`, "--w": `calc(${geo.widthPct}% - 3px)` } as CSSProperties}
        className={cn(
          "absolute bottom-1.5 left-[var(--l)] top-1.5 flex w-[var(--w)] min-w-[18px] flex-col justify-center gap-0.5 overflow-hidden rounded-md border border-l-[3px] px-2 text-left",
          block.bg,
          conflict ? "border-dashed border-rd-danger-500" : cn(block.border, block.accent),
          "hover:z-10 hover:shadow-rd-md"
        )}
      >
        <span className="flex min-w-0 items-center gap-1">
          {o.status === "RUNNING" ? <Dot tone="success" size={6} halo={false} pulse /> : null}
          {cancelled ? <Ban className="h-3 w-3 shrink-0 text-rd-danger-600" /> : null}
          {o.origin === "MANUAL" ? (
            // Marca a OP iniciada manualmente (vs agendada).
            <Hand
              className="h-3 w-3 shrink-0 text-text-tertiary"
              aria-label="Iniciada manualmente"
            />
          ) : null}
          <span
            className={cn(
              "truncate font-mono text-[11px] font-semibold",
              block.text,
              cancelled && "line-through"
            )}
          >
            {o.code ?? productsLabel(o)}
          </span>
          {overrun ? (
            // Badge de atraso: ícone + cor + label ("status nunca só por cor").
            // Sempre visível quando em overrun, mesmo que o tick do previsto caia fora da faixa.
            <span
              className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-sm bg-rd-warning-100 px-1 font-mono text-[9px] font-semibold text-rd-warning-700"
              aria-label={`Atrasada ${delay}`}
            >
              <TimerOff className="h-2.5 w-2.5" />
              {delay}
            </span>
          ) : null}
        </span>
        {geo.widthPct >= 6 ? (
          <span className="truncate text-[11px] text-text-secondary">
            {productsLabel(o)} <span className="text-text-tertiary">· {o.plannedQty} un</span>
          </span>
        ) : null}
      </button>
      {/* Trecho de overrun (expectedEnd → agora) com cor de atenção distinta do
          planejado; pointer-events-none deixa o clique chegar ao bloco abaixo. */}
      {og?.overrun ? (
        <div
          aria-hidden
          style={
            { "--ol": `${og.overrun.leftPct}%`, "--ow": `${og.overrun.widthPct}%` } as CSSProperties
          }
          className="bg-rd-warning-200/40 pointer-events-none absolute bottom-1.5 left-[var(--ol)] top-1.5 z-[1] w-[var(--ow)] rounded-r-md"
        />
      ) : null}
      {/* Tick/divisória no expectedEnd planejado, separando planejado × overrun (só na faixa). */}
      {og?.plannedEndPct != null ? (
        <div
          aria-hidden
          style={{ "--tk": `${og.plannedEndPct}%` } as CSSProperties}
          className="pointer-events-none absolute bottom-0.5 left-[var(--tk)] top-0.5 z-[1] w-0.5 -translate-x-1/2 bg-rd-warning-600"
        />
      ) : null}
      {tip ? <BlockTooltip o={o} tip={tip} /> : null}
    </>
  );
}
